import { useEffect, useRef } from 'react';
import { callRefresh } from '../api/refresh';
import { useAppStore } from '../state/store';

/**
 * When the user has OHLC loaded and has indicators but any indicator is missing
 * from indicatorSeries (e.g. just added an indicator), requests indicator data
 * from the refresh API so overlay/own-chart indicators can render.
 */
export function useFetchIndicatorSeriesWhenNeeded() {
  const primaryInstrumentId = useAppStore((s) => s.primaryInstrumentId);
  const instruments = useAppStore((s) => s.instruments);
  const ohlc = useAppStore((s) => s.ohlc);
  const indicators = useAppStore((s) => s.indicators);
  const indicatorSeries = useAppStore((s) => s.indicatorSeries);
  const setOhlcLoading = useAppStore((s) => s.setOhlcLoading);
  const setOhlcError = useAppStore((s) => s.setOhlcError);
  const loadFromRefreshResponse = useAppStore((s) => s.loadFromRefreshResponse);
  const inFlightRef = useRef(false);

  useEffect(() => {
    if (!primaryInstrumentId || instruments.length === 0 || indicators.length === 0) return;

    const hasOhlcForAll = instruments.every(
      (inst) =>
        ohlc?.[inst.id] &&
        Object.keys(ohlc[inst.id].series ?? {}).length > 0
    );
    if (!hasOhlcForAll) return;

    const seriesMap = indicatorSeries ?? {};
    const missing = indicators.some((ind) => !seriesMap[ind.id]);
    if (!missing) return;

    if (inFlightRef.current) return;
    inFlightRef.current = true;
    console.log('[useFetchIndicatorSeriesWhenNeeded] fetching: missing indicator series for', indicators.filter((ind) => !seriesMap[ind.id]).map((ind) => ({ id: ind.id, type: ind.type })));
    setOhlcLoading(true);
    setOhlcError(null);

    const state = useAppStore.getState();
    const request = {
      refresh: 'indicator' as const,
      instruments: state.instruments,
      primaryInstrumentId: state.primaryInstrumentId!,
      indicators: state.indicators,
    };

    callRefresh(request)
      .then((response) => {
        const keys = response.indicators && typeof response.indicators === 'object' && !Array.isArray(response.indicators) ? Object.keys(response.indicators) : [];
        const sample = keys.length && response.indicators ? response.indicators[keys[0]] : null;
        const sampleSize = typeof sample === 'object' && sample !== null && !Array.isArray(sample) ? Object.keys(sample).length : (typeof sample === 'number' ? 'number' : 'n/a');
        console.log('[useFetchIndicatorSeriesWhenNeeded] response.indicators keys:', keys, 'sample size:', sampleSize);
        loadFromRefreshResponse(response);
      })
      .catch((err) => {
        console.error('Indicator series refresh failed:', err);
        const message = err instanceof Error ? err.message : String(err);
        useAppStore.getState().setOhlcError(message);
      })
      .finally(() => {
        inFlightRef.current = false;
        useAppStore.getState().setOhlcLoading(false);
      });
  }, [
    primaryInstrumentId,
    instruments,
    ohlc,
    indicators,
    indicatorSeries,
    setOhlcLoading,
    setOhlcError,
    loadFromRefreshResponse,
  ]);
}
